import { GearStudioDesign, GearStudioImage, GearStudioPanel } from "@src/types";

type DesignPayload = {
    revisions?: GearStudioDesign[];
    data?: GearStudioDesign;
};

export type ImageSourceAsset = {
    images_data: {
        src: string;
        webp?: string | null;
    };
};

export const getPanelKey = (panel: GearStudioPanel) => `${panel.position}::${panel.name}`;

export const getImageSrc = (image: GearStudioImage | ImageSourceAsset) => {
    return image.images_data.webp || image.images_data.src;
};

export const sortDesignsByRevision = (items: GearStudioDesign[]) => {
    return [...items].sort((left, right) => {
        if (left.revision_number !== right.revision_number) {
            return left.revision_number - right.revision_number;
        }
        return new Date(left.created_at).getTime() - new Date(right.created_at).getTime();
    });
};

const clonePanelImages = (images: GearStudioImage[]): GearStudioImage[] => {
    return images.map((image) => ({
        ...image,
        images_data: {
            ...image.images_data,
        },
    }));
};

export const mergeDesignWithSourcePanelImages = (
    target: GearStudioDesign,
    source: GearStudioDesign,
): GearStudioDesign => {
    const sourcePanelsByKey = new Map(source.panels.map((panel) => [getPanelKey(panel), panel]));
    return {
        ...target,
        panels: target.panels.map((panel) => {
            const sourcePanel = sourcePanelsByKey.get(getPanelKey(panel));
            if (!sourcePanel || sourcePanel.images.length === 0) {
                return panel;
            }
            return {
                ...panel,
                images: clonePanelImages(sourcePanel.images),
            };
        }),
    };
};

export const mergeIncomingWithExistingDesigns = (
    incoming: GearStudioDesign[],
    existing: GearStudioDesign[],
) => {
    const existingMap = new Map(existing.map((design) => [design.id, design]));
    return incoming.map((incomingDesign) => {
        const existingDesign = existingMap.get(incomingDesign.id);
        if (!existingDesign) {
            return incomingDesign;
        }
        return mergeDesignWithSourcePanelImages(incomingDesign, existingDesign);
    });
};

export const getRevisionsFromPayload = (payload: unknown): GearStudioDesign[] => {
    if (!payload || typeof payload !== "object") {
        return [];
    }

    const normalizedPayload = payload as DesignPayload;
    if (Array.isArray(normalizedPayload.revisions) && normalizedPayload.revisions.length > 0) {
        return sortDesignsByRevision(normalizedPayload.revisions);
    }

    if (normalizedPayload.data && typeof normalizedPayload.data === "object") {
        return [normalizedPayload.data];
    }

    return [];
};

export const loadImageDimensions = (src: string): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
        image.onerror = () => reject(new Error("Failed to load image."));
        image.src = src;
    });
};
